import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "@/lib/api.ts";
import { Alert, Button, Text, TextInput, Title } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router";
import { z, ZodType } from "zod";

type ForgotPasswordForm = {
  email: string;
};

const forgotPasswordFormSchema: ZodType<ForgotPasswordForm> = z.object({
  email: z
    .string()
    .min(1, { message: "Email address is required" })
    .email("Invalid email address"),
});

export function ForgotPassword() {
  const [submitted, setSubmitted] = useState(false);
  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      email: "",
    },
  });
  const errors = form.formState.errors;

  const mutation = useMutation({
    mutationFn: (forgotPasswordForm: ForgotPasswordForm) => {
      return api.post("auth/forgot-password", {
        ...forgotPasswordForm,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    retry: 1,
  });

  const onSubmit: SubmitHandler<ForgotPasswordForm> = (data) => {
    mutation.mutate(data);
  };

  const icon = <CircleInfo />;

  return (
    <div className="mx-auto grid w-[350px] gap-6">
      <div className="grid gap-1 text-center">
        <Title order={1}>Reset password</Title>
        {!submitted && <Text c="gray">Enter your email to reset password</Text>}
      </div>

      {submitted && (
        <Alert color="indigo" icon={icon}>
          Please check your email for further instructions.
        </Alert>
      )}

      {!submitted && (
        <form className="grid gap-6" onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput
            {...form.register("email")}
            type="email"
            name="email"
            withAsterisk
            label="Email address"
            size="md"
            error={errors.email?.message}
          />

          <Button
            loading={mutation.isPending}
            disabled={mutation.isPending}
            type="submit"
            fullWidth
            size="md"
          >
            Continue
          </Button>
        </form>
      )}

      <Text ta="center">
        Oh, you did remember your password?{" "}
        <Link to="/auth/login" className="underline">
          Sign in
        </Link>
      </Text>
    </div>
  );
}
